import { CoinsVal } from "@components/icons/coins";
import { LoraTile } from "@components/lora-tile";
import { RelationshipImageThumbnail } from "@components/relationship-image-thumbnail";
import { ResponsiveModal } from "@components/responsive-modal";
import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { ImagesType } from "@features/images/type";
import { useImage, useImages } from "@features/images/use-images";
import { useImageActions } from "@features/images/use-images-actions";
import { getPackPreview } from "@features/images/use-packs";
import { useRelatedImages } from "@features/images/use-related-images";
import { LorasType } from "@features/loras/type";
import { getLoraPreview } from "@features/loras/use-loras";
import { usePlanBetterOrEq } from "@features/plans";
import { useUser } from "@features/user/use-user";
import { useNavigateAlt } from "@features/utils";
import {
  ArchiveBoxXMarkIcon,
  ArrowDownCircleIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  HeartIcon,
} from "@heroicons/react/16/solid";
import { MagicWandIcon } from "@radix-ui/react-icons";
import { Button, Flex, Heading, Text, Tooltip } from "@radix-ui/themes";
import _ from "lodash";
import { ShuffleIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ProjectSelector } from "../../../camera/components/form-footer";

interface StudioInformationProps {
  image: ImagesType;
  model?: LorasType;
  style?: any;
  pack?: any;
  closestImageId?: string;
}

export const StudioInformation = ({
  image,
  model,
  style,
  pack,
  closestImageId,
}: StudioInformationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigateAlt();
  const { site } = useParams();
  const { user } = useUser();

  const {
    remove,
    restore,
    download,
    upscale,
    magic,
    changeProjectId,
    setPendingUpdate,
  } = useImage(image.id);
  const { toggleFavorite } = useImageActions(image.id);
  const { images: relatedImages } = useImages("from:" + image.id);
  const { data: relatedData } = useRelatedImages(image.id);

  // Upscale modal state
  const [isUpscaleModalOpen, setIsUpscaleModalOpen] = useState(false);
  const [upscaleLoading, setUpscaleLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const isPlanBetterOrEq = usePlanBetterOrEq();
  const has4MP = isPlanBetterOrEq("essential");
  const has9MP = isPlanBetterOrEq("essential");
  const has16MP = isPlanBetterOrEq("essential");
  const [resolution, setResolution] = useState<"2" | "3" | "4">(
    has4MP ? "2" : has9MP ? "3" : has16MP ? "4" : "2",
  );

  const handleUpscale = async () => {
    setUpscaleLoading(true);
    try {
      await upscale(parseInt(resolution));
      setIsUpscaleModalOpen(false);
    } catch (e) {
      console.error("Upscale failed", e);
    }
    setUpscaleLoading(false);
  };

  const handleMagicImprove = async () => {
    setMagicLoading(true);
    try {
      await magic();
    } catch (e) {
      console.error("Magic improve failed", e);
    }
    setMagicLoading(false);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t("keyboard.pasted_to_clipboard") + ": " + label);
  };

  const handleLoraClick = (loraId: string, loraType: string) => {
    if (loraType === "model") {
      navigate(`/${site}/camera/new?mode=classic&model=${loraId}`);
    } else if (loraType === "style") {
      navigate(`/${site}/camera/new?mode=classic&style=${loraId}`);
    }
  };

  const handlePackClick = (packId: string) => {
    navigate(`/${site}/camera/new?mode=pack&pack=${packId}`);
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="space-y-2 w-full">
        <div className="grid grid-cols-3 gap-2">
          <Tooltip content={t("components.camera-roll-image.download")}>
            <Button
              size={"3"}
              onClick={() => download()}
              color="green"
              variant="soft"
            >
              <ArrowDownCircleIcon className="w-5 h-5" />
            </Button>
          </Tooltip>
          <Tooltip
            content={
              image.favorite
                ? t("components.camera-roll-image.unfavorite")
                : t("components.camera-roll-image.favorite")
            }
          >
            <Button
              size={"3"}
              onClick={() => toggleFavorite(image, setPendingUpdate)}
              color={image.favorite ? "red" : "gray"}
              variant={image.favorite ? "solid" : "soft"}
            >
              <HeartIcon className="w-5 h-5" />
            </Button>
          </Tooltip>
          <Tooltip
            content={
              image.archived
                ? t("components.camera-roll-image.restore")
                : t("components.camera-roll-image.archive")
            }
          >
            <Button
              size={"3"}
              color={image.archived ? "indigo" : "gray"}
              variant="soft"
              onClick={() => (image.archived ? restore() : remove())}
            >
              {image.archived && <HandThumbUpIcon className="w-5 h-5" />}
              {!image.archived && <HandThumbDownIcon className="w-5 h-5" />}
            </Button>
          </Tooltip>
        </div>

        {/* Reuse Parameters and Original Image Buttons */}
        {!(
          (image.type && image.type !== "image") ||
          image.sub_type === "upscale"
        ) && (
          <>
            {(user.data?.credits_used || 0) > 50 &&
              (model?.subtype === "avatar" ||
                image?.steps?.some((a) => a.faces?.length)) && (
                <div>
                  <Tooltip content={t("studio.magic_improve_description")}>
                    <UpgradeNeeded minimumCredits={32}>
                      <Button
                        size={"2"}
                        className="w-full hue-animation"
                        variant="solid"
                        loading={magicLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMagicImprove();
                        }}
                      >
                        <MagicWandIcon className="w-4 h-4" />
                        {t("studio.magic_improve")} <CoinsVal val={32} />
                      </Button>
                    </UpgradeNeeded>
                  </Tooltip>
                </div>
              )}
            {model?.id && (
              <>
                <Button
                  size={"2"}
                  className="w-full"
                  variant="soft"
                  onClick={(e) => {
                    navigate(
                      `/${site}/camera/new?mode=image&similarity=20&image=` +
                        encodeURIComponent(image.url) +
                        "&model=" +
                        encodeURIComponent(model?.id || ""),
                      e,
                    );
                  }}
                >
                  <ShuffleIcon className="w-4 h-4" />
                  {t("studio.generate_variations")}
                </Button>
                {["classic", "tryon", "image", "haircut", "pack"].includes(
                  image.sub_type,
                ) && (
                  <Button
                    size={"2"}
                    className="w-full"
                    variant="soft"
                    onClick={(e) => {
                      navigate(
                        `/${site}/camera/new?mode=${
                          image.pack_id
                            ? "pack"
                            : image.steps[0].image_to_image?.url
                              ? "image"
                              : image.sub_type
                        }&similarity=20&image=` +
                          encodeURIComponent(
                            ["tryon", "haircut"].includes(image.sub_type)
                              ? `${
                                  image.steps.find(
                                    (s) => s.mode === image.sub_type,
                                  )?.clothing?.url ||
                                  image.steps.find(
                                    (s) => s.mode === image.sub_type,
                                  )?.haircut?.url
                                }`
                              : image.steps[0].image_to_image?.url || image.url,
                          ) +
                          "&model=" +
                          encodeURIComponent(model?.id || "") +
                          "&prompt=" +
                          encodeURIComponent(image.steps[0].prompt || "") +
                          "&pack=" +
                          encodeURIComponent(image.pack_id || "") +
                          "&format=auto",
                        e,
                      );
                    }}
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    {t("studio.reuse_parameters")}
                  </Button>
                )}
              </>
            )}
            {/* Upscale Button */}
            {relatedImages.data?.some((a: any) => a.sub_type === "upscale") && (
              <Button
                size={"2"}
                className="w-full"
                variant="soft"
                onClick={() => {
                  navigate(
                    "/" +
                      site +
                      "/studio/" +
                      relatedImages.data?.find(
                        (a: any) => a.sub_type === "upscale",
                      )?.id || "",
                  );
                }}
              >
                <ArrowsPointingOutIcon className="w-4 h-4" />
                {t("studio.upscale.go_to_upscaled")}
              </Button>
            )}
            <Button
              size={"2"}
              className="w-full"
              variant="soft"
              onClick={() => setIsUpscaleModalOpen(true)}
            >
              <ArrowsPointingOutIcon className="w-4 h-4" />
              {t("studio.upscale.title")}
            </Button>
          </>
        )}

        {image.from_image_id && (
          <Button
            size={"2"}
            className="w-full"
            variant="soft"
            onClick={() =>
              navigate("/" + site + "/studio/" + image.from_image_id)
            }
          >
            {t("studio.go_to_original")}
          </Button>
        )}

        {image.archived && (
          <Button
            size={"2"}
            className="w-full"
            color="red"
            variant="soft"
            onClick={() =>
              remove(closestImageId ? `/${site}/studio/${closestImageId}` : "")
            }
          >
            <ArchiveBoxXMarkIcon className="w-4 h-4" />
            {t("components.camera-roll-image.delete")}
          </Button>
        )}
      </div>

      {/* Project Section */}
      <div className="space-y-2">
        <Heading size="3">{t("studio.project")}</Heading>
        <ProjectSelector
          value={image.project_id || "none"}
          onChange={changeProjectId}
        />
      </div>

      {/* Information Section */}
      {image?.type !== "video" && !!model?.id && (
        <div className="space-y-2">
          <Heading size="3">{t("studio.image_information")}</Heading>
          <div className="flex flex-wrap gap-2">
            {model?.id && (
              <div
                onClick={() => handleLoraClick(model.id, model.type)}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                <LoraTile
                  className="!w-24 !h-24"
                  key={model.id}
                  name={model.name}
                  picture={getLoraPreview(model.id, model.preview_status)}
                  type={model.type}
                  subtype={model.subtype}
                />
              </div>
            )}
            {style?.id && (
              <div
                onClick={() => handleLoraClick(style.id, style.type)}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                <LoraTile
                  className="!w-24 !h-24"
                  key={style.id}
                  name={style.name}
                  picture={getLoraPreview(style.id, style.preview_status)}
                  type={style.type}
                  subtype={style.subtype}
                />
              </div>
            )}
            {pack?.id && (
              <div
                onClick={() => handlePackClick(pack.id)}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                <LoraTile
                  className="!w-32 !h-24"
                  name={pack.id}
                  picture={getPackPreview(pack.id)}
                  type={"pack"}
                />
              </div>
            )}
          </div>

          {/* Prompts Dropdown */}
          {false &&
            !image.pack_id &&
            (image.steps[0].prompt || image.steps[0].augmented_prompt) && (
              <div className="space-y-2">
                {image.steps[0].prompt && (
                  <Button
                    size="2"
                    variant="soft"
                    className="w-full"
                    onClick={() =>
                      copyToClipboard(
                        image.steps[0].prompt,
                        t("studio.original_prompt"),
                      )
                    }
                  >
                    {t("studio.copy_original_prompt")}
                  </Button>
                )}
                {image.steps[0].augmented_prompt && (
                  <Button
                    size="2"
                    variant="soft"
                    className="w-full"
                    onClick={() =>
                      copyToClipboard(
                        image.steps[0].augmented_prompt,
                        t("studio.augmented_prompt"),
                      )
                    }
                  >
                    {t("studio.copy_augmented_prompt")}
                  </Button>
                )}
              </div>
            )}
        </div>
      )}

      {/* Image Relationships */}
      {relatedData && (
        <div className="space-y-2">
          <Heading size="3">{t("studio.relationships.title")}</Heading>

          {/* Parent Image */}
          {relatedData.parentImage && (
            <div className="space-y-2">
              <Text size="2" weight="medium">
                {t("studio.relationships.original")}
              </Text>
              <div className="grid grid-cols-6 gap-2">
                <RelationshipImageThumbnail
                  image={relatedData.parentImage}
                  className="aspect-square"
                />
              </div>
            </div>
          )}

          {/* Siblings */}
          {relatedData.pathToTarget.length > 0 &&
            (() => {
              const siblings = _.sortBy(
                relatedData.siblingGroups[
                  relatedData.pathToTarget[
                    relatedData.pathToTarget.length - 2
                  ] || "root"
                ] || [],
                (a) => a.created_at,
              );

              if (siblings.length === 0) return null;

              return (
                <div className="space-y-2">
                  <Text size="2" weight="medium">
                    {t("studio.relationships.siblings")}
                  </Text>
                  <div className="grid grid-cols-6 gap-2">
                    {siblings.map((sibling) => (
                      <RelationshipImageThumbnail
                        key={sibling.id}
                        image={sibling}
                        isActive={sibling.id === image.id}
                        className="aspect-square"
                      />
                    ))}
                  </div>
                </div>
              );
            })()}

          {/* Children */}
          {relatedData.immediateChildren.length > 0 && (
            <div className="space-y-2">
              <Text size="2" weight="medium">
                {t("studio.relationships.children")}
              </Text>
              <div className="grid grid-cols-6 gap-2">
                {_.sortBy(
                  relatedData.immediateChildren,
                  (a) => a.created_at,
                ).map((child) => (
                  <RelationshipImageThumbnail
                    key={child.id}
                    image={child}
                    className="aspect-square"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upscale Modal */}
      <ResponsiveModal
        isOpen={isUpscaleModalOpen}
        onClose={() => setIsUpscaleModalOpen(false)}
        title={t("studio.upscale.title")}
        height="auto"
      >
        <Flex direction="column" gap="4">
          <Text size="2" color="gray">
            {t("studio.upscale.description")}
          </Text>

          <ResponsiveSelect
            value={resolution}
            onChange={(value) => setResolution(value as "2" | "3" | "4")}
            items={[
              {
                value: "2",
                label: t("studio.upscale.resolution_2x"),
              },
              {
                value: "3",
                label: t("studio.upscale.resolution_3x"),
              },
              {
                value: "4",
                label: t("studio.upscale.resolution_4x"),
              },
            ]}
            className="w-full"
            renderTrigger={(value) => <>{value?.label}</>}
          />

          <Flex gap="3" justify="end" className="mt-4">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setIsUpscaleModalOpen(false)}
              disabled={upscaleLoading}
            >
              {t("general.cancel")}
            </Button>
            <UpgradeNeeded minimumCredits={16}>
              <Button
                variant="solid"
                onClick={handleUpscale}
                disabled={upscaleLoading}
                loading={upscaleLoading}
              >
                <ArrowsPointingOutIcon className="w-4 h-4" />
                {t("components.camera-roll-image.upscale")}{" "}
                <CoinsVal val={32} />
              </Button>
            </UpgradeNeeded>
          </Flex>
        </Flex>
      </ResponsiveModal>
    </div>
  );
};
